import {
  selectAllCompanyUsersById,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";
import Button from "app/storybookComponents/Button";
import { getS } from "utils/helperFunctions";
import { useAppSelector } from "utils/redux/hooks";
import ReassignmentBox from "./ReassignmentBox";
import { useState } from "react";
import { Separator } from "app/shadcnComponents/ui/separator";
import { ChangesSummary, DeactivateUserTeamSetupMap, ErrorMap } from "./types";

interface Props {
  userAccountIdToDeactivate?: number | null;
  teamIds: number[];
  onStepForward: () => void;
  onStepBack: () => void;
  onUpdateChangesSummary?: (changesSummary: ChangesSummary) => void;
  initialTeamSetupMap?: DeactivateUserTeamSetupMap;
}

const ReassignTeamLeadersStep = ({
  userAccountIdToDeactivate,
  teamIds,
  onStepForward,
  onStepBack,
  onUpdateChangesSummary,
  initialTeamSetupMap,
}: Readonly<Props>) => {
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const teamsById = useAppSelector(selectTeamsByTeamId);
  const [teamSetupMap, setTeamSetupMap] = useState<DeactivateUserTeamSetupMap>(
    initialTeamSetupMap ?? {}
  );
  const [teamErrorMap, setTeamErrorMap] = useState<ErrorMap>({});

  const user = userAccountIdToDeactivate
    ? usersById[userAccountIdToDeactivate]
    : null;
  const userName = `${user?.firstName} ${user?.lastName}`.trim() || "User";

  const clearTeamError = (teamId: number) => {
    if (teamErrorMap[teamId]) {
      setTeamErrorMap((prev) => {
        const { [teamId]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const getReassignmentBoxes = () => {
    return teamIds.map((teamId) => {
      const team = teamsById[teamId];
      const totalTeamMembers = team?.teamMemberIds?.length ?? 0;
      return (
        <ReassignmentBox
          key={teamId}
          title={team.teamName}
          description={`${totalTeamMembers} member${getS(
            totalTeamMembers
          )} • Manual team`}
          selectedUserAccountId={
            teamSetupMap[teamId]?.newTeamLeaderUserAccountId
          }
          setSelectedUserAccountId={(userAccountId) => {
            setTeamSetupMap({
              ...teamSetupMap,
              [teamId]: {
                ...teamSetupMap[teamId],
                newTeamLeaderUserAccountId: userAccountId,
              },
            });
            clearTeamError(teamId);
          }}
          isAlternativeOptionSelected={teamSetupMap[teamId]?.deleteTeam}
          onAlternativeOptionSelected={(isAlternativeOptionSelected) => {
            setTeamSetupMap({
              ...teamSetupMap,
              [teamId]: {
                ...teamSetupMap[teamId],
                deleteTeam: isAlternativeOptionSelected,
              },
            });
            clearTeamError(teamId);
          }}
          labels={{
            primaryLabel: {
              title: "Assign new team leader",
            },
            secondaryLabel: {
              title: "Delete this team",
            },
          }}
          error={teamErrorMap[teamId]}
          excludeUserAccountIds={
            userAccountIdToDeactivate ? [userAccountIdToDeactivate] : []
          }
        />
      );
    });
  };

  const onBackClick = () => {
    onStepBack();
  };

  const getTeamValidationErrors = (): ErrorMap => {
    const errors: ErrorMap = {};
    teamIds.forEach((teamId) => {
      const teamSetupState = teamSetupMap[teamId];
      if (
        !teamSetupState ||
        (!teamSetupState.deleteTeam &&
          !teamSetupState.newTeamLeaderUserAccountId)
      ) {
        errors[teamId] = {
          errorMessage: "*You need to select a team leader or delete this team",
        };
      }
    });
    return errors;
  };

  const onContinueClick = async () => {
    // Checking for validation errors
    const errors = getTeamValidationErrors();
    setTeamErrorMap(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    onUpdateChangesSummary?.({
      teamChanges: teamSetupMap,
    });
    onStepForward();
  };

  return (
    <>
      <p>
        <b>
          {userName} leads {teamIds.length} manual team{getS(teamIds.length)}.
        </b>{" "}
        What should happen to {teamIds.length === 1 ? "this" : "these"} manual
        team{getS(teamIds.length)}?
      </p>
      {getReassignmentBoxes()}
      <Separator />
      <div className="flex justify-end gap-2">
        <Button onClick={onBackClick} variant="secondary-gray">
          Back
        </Button>
        <Button onClick={onContinueClick}>Continue</Button>
      </div>
    </>
  );
};

export default ReassignTeamLeadersStep;
